import { getParent, Instance, types } from "mobx-state-tree";
import { NumberModel } from "../../mobxDataModels/numberModel";
import { StringModel } from "../../mobxDataModels/stringModel";
import { defaultValues } from "../base";
import { WorkloadDraftMobx } from "./workload.draft";
import { SelectModel } from "../../mobxDataModels/selectModel";

export const WorkloadDraftRolloutOptionsModel = types
  .model({
    _configure: types.optional(types.boolean, false),
    _minReadySeconds: types.optional(types.number, defaultValues.workload.rolloutOptions.minReadySeconds.default),
    _maxSurgeReplicas: types.optional(types.string, ""),
    _maxUnavailableReplicas: types.optional(types.string, ""),
    _scalingPolicy: types.optional(types.string, defaultValues.workload.rolloutOptions.scalingPolicy.default),
    _terminationGracePeriodSeconds: types.optional(types.number, 90),
    configure: types.optional(types.boolean, false),
    minReadySeconds: types.optional(StringModel, () =>
      NumberModel.create({ label: "Min Ready Seconds", isRequired: true, min: 0 }),
    ),
    maxSurgeReplicas: types.optional(StringModel, () =>
      StringModel.create({
        label: "Max Surge Replicas",
        initialValue: "",
        // TODO add the regex
      }),
    ),
    maxUnavailableReplicas: types.optional(StringModel, () =>
      StringModel.create({
        label: "Max Unavailable Replicas",
        initialValue: "",
      }),
    ),
    scalingPolicy: types.optional(SelectModel, () =>
      SelectModel.create({
        label: "Scaling Policy",
        initialValue: defaultValues.workload.rolloutOptions.scalingPolicy.default,
        options: [
          {
            label: "OrderedReady",
            value: "OrderedReady",
          },
          {
            label: "Parallel",
            value: "Parallel",
          },
        ],
      }),
    ),
    terminationGracePeriodSeconds: types.optional(StringModel, () =>
      NumberModel.create({ label: "Termination Grace Period Seconds", min: 0, max: 900, initialValue: 90 }),
    ),
  })
  .actions((self) => ({
    reset() {
      self.configure = self._configure;
      self.minReadySeconds.setInitialValue(String(self._minReadySeconds));
      self.maxSurgeReplicas.setInitialValue(self._maxSurgeReplicas);
      self.maxUnavailableReplicas.setInitialValue(self._maxUnavailableReplicas);
      self.scalingPolicy.setInitialValue(self._scalingPolicy);
      self.terminationGracePeriodSeconds.setInitialValue(String(self._terminationGracePeriodSeconds));
    },
    setConfigure(value: boolean) {
      self.configure = value;
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.reset();
    },
    confirm() {
      self._configure = self.configure;
      self._minReadySeconds = Number(self.minReadySeconds.value);
      self.minReadySeconds.confirm();
      self._maxSurgeReplicas = self.maxSurgeReplicas.value;
      self.maxSurgeReplicas.confirm();
      self._maxUnavailableReplicas = self.maxUnavailableReplicas.value;
      self.maxUnavailableReplicas.confirm();
      self._scalingPolicy = self.scalingPolicy.value;
      self.scalingPolicy.confirm();
      self._terminationGracePeriodSeconds = Number(self.terminationGracePeriodSeconds.value);
      self.terminationGracePeriodSeconds.confirm();
    },
  }))
  .views((self) => ({
    get isDirty() {
      let res = false;
      const parent: WorkloadDraftMobx = getParent(self);
      if (self._configure !== self.configure) res = true;
      if (self.configure) {
        if (!parent.isStateful) {
          if (self.maxUnavailableReplicas.isDirty) res = true;
        }
        if (self.minReadySeconds.isDirty) res = true;
        if (self.maxSurgeReplicas.isDirty) res = true;
        if (self.scalingPolicy.isDirty) res = true;
        if (self.terminationGracePeriodSeconds.isDirty) res = true;
      }
      return res;
    },
    get isValid() {
      let res = true;
      const parent: WorkloadDraftMobx = getParent(self);
      if (self.configure) {
        if (!self.minReadySeconds.isValid) res = false;
        if (!self.maxSurgeReplicas.isValid) res = false;
        if (!self.terminationGracePeriodSeconds.isValid) res = false;
        if (!parent.isStateful) {
          if (!self.maxUnavailableReplicas.isValid) res = false;
        }
      }
      return res;
    },
    get asObject() {
      const parent: WorkloadDraftMobx = getParent(self);
      let res: any = {
        scalingPolicy: self.scalingPolicy.value,
      };
      if (self.minReadySeconds.value.length > 0) {
        res.minReadySeconds = Number(self.minReadySeconds.value);
      }
      if (self.maxSurgeReplicas.value.length > 0) {
        res.maxSurgeReplicas = self.maxSurgeReplicas.value;
      }
      if (self.terminationGracePeriodSeconds.value.length > 0) {
        res.terminationGracePeriodSeconds = Number(self.terminationGracePeriodSeconds.value);
      }
      if (!parent.isStateful) {
        if (self.maxUnavailableReplicas.value.length > 0) {
          res.maxUnavailableReplicas = self.maxUnavailableReplicas.value;
        }
      }
      return res;
    },
  }));
export interface WorkloadDraftRolloutOptionsMobx extends Instance<typeof WorkloadDraftRolloutOptionsModel> {}
